/**
 * Hook for managing unit marketing profile — GET and PUT operations.
 * Reads/writes the attributes typed-JSON bag per ADR-LS-008.
 *
 * Backed by React Query so every consumer of one Unit's profile (the Unit Marketing tab, the
 * guide's Marketing step, the guide rail's completeness) reads one cache entry: a save updates it in
 * place and the rail and the preview see the saved data (BR-110).
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { UnitMarketingProfile } from '../types/unit-marketing.types';

interface UseMarketingProfileReturn {
  profile: UnitMarketingProfile | null;
  isLoading: boolean;
  isSaving: boolean;
  /** Load error only; a failed save rejects the saveProfile promise instead. */
  error: string | null;
  saveProfile: (updates: Partial<UnitMarketingProfile>) => Promise<void>;
  refetch: () => Promise<void>;
}

const DEFAULT_PROFILE: UnitMarketingProfile = {
  unitId: '',
  description: null,
  propertyType: null,
  attributes: null,
  virtualTourUrl: null,
  amenities: [],
  createdAt: null,
  updatedAt: null,
};

const unitMarketingProfileKey = (unitId: string) => ['unit-marketing-profile', unitId] as const;

async function fetchMarketingProfile(unitId: string): Promise<UnitMarketingProfile> {
  try {
    const response = await apiFetch<UnitMarketingProfile>(`/landlord/api/units/${unitId}/marketing-profile`);
    return response ?? { ...DEFAULT_PROFILE, unitId };
  } catch (err) {
    // 404 means no profile yet — use empty defaults
    if (err instanceof Error && err.message.includes('404')) return { ...DEFAULT_PROFILE, unitId };
    throw err;
  }
}

export function useMarketingProfile(unitId: string): UseMarketingProfileReturn {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const query = useQuery({
    queryKey: unitMarketingProfileKey(unitId),
    queryFn: () => fetchMarketingProfile(unitId),
    enabled: !!unitId,
    retry: false,
  });
  const { data, refetch: refetchQuery } = query;

  const saveProfile = useCallback(
    async (updates: Partial<UnitMarketingProfile>) => {
      if (!unitId) return;
      try {
        setIsSaving(true);
        const current = data ?? { ...DEFAULT_PROFILE, unitId };
        // Merge attributes at the top level — attributes is a single bag,
        // so deep-merge is not needed; the caller provides the full updated bag.
        const merged: Partial<UnitMarketingProfile> = { ...current, ...updates, unitId };
        // Strip null attributes before sending — the API schema rejects null
        // (it accepts the field as optional/object, not nullable). When attributes
        // is null it means "no profile yet"; omitting the key is correct here.
        let body: Partial<UnitMarketingProfile> = merged;
        if (merged.attributes === null) {
          const { attributes: _omitted, ...rest } = merged as Required<UnitMarketingProfile>;
          void _omitted; // Suppress unused variable lint warning
          body = rest;
        }
        const response = await apiFetch<UnitMarketingProfile>(`/landlord/api/units/${unitId}/marketing-profile`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        queryClient.setQueryData(unitMarketingProfileKey(unitId), response);
      } catch (err) {
        throw new Error(err instanceof Error ? err.message : 'Failed to save marketing profile');
      } finally {
        setIsSaving(false);
      }
    },
    [unitId, data, queryClient]
  );

  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  return {
    profile: data ?? (query.error ? { ...DEFAULT_PROFILE, unitId } : null),
    isLoading: query.isLoading,
    isSaving,
    error: query.error instanceof Error ? query.error.message : query.error ? 'Failed to load marketing profile' : null,
    saveProfile,
    refetch,
  };
}
